/**
 * ExpedienteTab — Expediente clínico del paciente (rediseño con navegación lateral).
 *
 * Layout de dos paneles: una lista lateral izquierda para navegar entre las
 * secciones del expediente y un panel de contenido derecho con scroll propio
 * (esto resuelve los problemas de scroll del diseño anterior, donde todo iba en
 * una sola columna muy larga).
 *
 * Secciones:
 *   0. Resumen     — resumen clínico del paciente EN PROSA (última consulta,
 *                    diagnósticos, alergias, medicamentos e info disponible).
 *   1. Diagnósticos
 *   2. Medicamentos
 *   3. Alergias
 *   4. Signos vitales (si hay)
 *   5. Órdenes (si hay)
 *   6. Vacunas (MINSA)
 *   7. Laboratorio (MINSA)
 *   8. Próximas citas (SEIS)
 *   9. Historial de consultas — lista; al hacer clic se muestra el detalle
 *      completo de la consulta (todas las secciones, como en /historial).
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  Calendar,
  ChevronRight,
  Clock,
  ExternalLink,
  FileText,
  FlaskConical,
  Heart,
  Loader2,
  Microscope,
  Pill,
  ShieldAlert,
  Sparkles,
  Stethoscope,
  Syringe,
  Activity,
  User,
  MessageSquareText,
  Brain,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import type { Timestamp } from 'firebase/firestore';
import { auth, functions } from '../../config/firebase';
import type { FHIRItem, PatientContext } from '../../services/medicalAI';
import { type ExternalState, VacunasBody, LabsBody, CitasBody } from './ExpedienteExterno';
import { Avatar } from '../ui/Avatar';
import styles from './ExpedienteTab.module.css';

/* ── Types ── */

interface HistorialItem {
  id: string;
  pacienteNombre: string;
  motivoConsulta?: string;
  medicoNombre?: string;
  medicoId?: string;
  salaNombre?: string;
  estado: string;
  disposicion?: string;
  duracionSegundos?: number;
  notas?: string;
  creadoEn?: Timestamp;
  iniciadaEn?: Timestamp;
  finalizadaEn?: Timestamp;
}

interface DetalleSoap {
  subjective?: string;
  objective?: string;
  assessment?: string;
  plan?: string;
  evolution?: string;
  education?: string;
}

/** Matches actual cloud function response from portal_obtenerDetalleConsulta */
interface DetalleConsulta {
  consulta: {
    id: string;
    pacienteNombre?: string;
    motivoConsulta?: string;
    medicoNombre?: string;
    salaNombre?: string;
    estado?: string;
    disposicion?: string;
    duracionSegundos?: number;
    notas?: string;
    creadoEn?: Timestamp;
    iniciadaEn?: Timestamp;
    finalizadaEn?: Timestamp;
  };
  notas?: Array<{
    id: string;
    tipo?: string;
    medicoId?: string;
    soap?: DetalleSoap;
    fhir?: FHIRItem[];
    alerts?: string[];
    healthEducation?: string[] | string;
    creadoEn?: Timestamp;
  }>;
  transcripcion?: {
    segmentos: number;
    porHablante: Array<{ hablante: string; fragmentos: number; extracto: string }>;
    totalPalabras: number;
  };
}

/* ── Props ── */

interface TriageEventoPrevio {
  id: string;
  resultado: string | null;
  nivel: number | null;
  nivelNombre: string | null;
  region: { codigo?: string | null; descripcion?: string | null } | null;
  salaNombre: string | null;
  motivoConsulta: string | null;
  fecha: string | null;
  creadoEnMs: number | null;
}

export interface ExpedienteTabProps {
  pacienteId?: string | null;
  /** Firebase UID del paciente — ayuda a resolver la cédula (users/{uid}) cuando
   *  pacienteId quedó guardado como UID en vez de cédula. */
  pacienteUid?: string | null;
  patientContext: PatientContext;
  /** Foto de perfil del paciente (denormalizada en la consulta). */
  pacienteFotoURL?: string | null;
  fhir: FHIRItem[];
  currentConsultaId?: string;
}

/* ── Smart categorisation (shared logic with StructuredPlan) ── */

const ICD10_RE = /^[A-Z]\d{2}(\.\d{1,2})?$/i;

const LAB_KW = /hemograma|orina|glicemia|glucosa|creatinina|bun|electrolitos|hemoglobina|perfil|lipídico|tsh|vih|cultivo|urocultivo|pcr|troponina|bilirrubina|transaminasa|alt|ast|fosfatasa|ferritina|vitamina|ácido\s*úrico/i;
const IMG_KW = /placa|radiografía|rayos\s*x|rx\s|tomografía|tac|resonancia|rmn|ecografía|ultrasonido|mamografía|ecocardiograma/i;
const MED_KW = /paracetamol|ibuprofeno|diclofenaco|amoxicilina|azitromicina|cefalexina|metformina|losartán|enalapril|amlodipino|atorvastatina|omeprazol|salbutamol|loratadina|prednisona|insulina|levotiroxina|tableta|cápsula|comprimido|jarabe|mg\b|ml\b/i;
const REF_KW = /interconsulta|referencia|derivación|especialista|cardio|neuro|gastro|endocrino|nefro|neumol|dermat|otorrino|oftalmo|urolog|gineco|traumat|psiqui/i;

type ItemCategory = 'diagnosis' | 'medication' | 'order' | 'other';

function categorise(item: FHIRItem): ItemCategory {
  if (item.type === 'medication') return 'medication';
  if (['labOrder', 'labResult', 'imagingOrder', 'referral', 'procedure'].includes(item.type)) return 'order';
  if (['allergy', 'familyHistory', 'personalHistory'].includes(item.type)) return 'other';

  const text = `${item.display} ${item.details ?? ''}`;
  if (MED_KW.test(text)) return 'medication';
  if (LAB_KW.test(text) || IMG_KW.test(text) || REF_KW.test(text)) return 'order';
  return 'diagnosis';
}

type OrderSubType = 'lab' | 'imaging' | 'referral' | 'procedure';

function orderSubType(item: FHIRItem): OrderSubType {
  if (item.type === 'labOrder' || item.type === 'labResult') return 'lab';
  if (item.type === 'imagingOrder') return 'imaging';
  if (item.type === 'referral') return 'referral';
  const text = `${item.display} ${item.details ?? ''}`;
  if (IMG_KW.test(text)) return 'imaging';
  if (REF_KW.test(text)) return 'referral';
  return 'lab';
}

const ORDER_LABELS: Record<OrderSubType, string> = {
  lab: 'Lab',
  imaging: 'Imagen',
  referral: 'Referencia',
  procedure: 'Procedimiento',
};

/* ── Agregación de hallazgos a través del historial (fecha + médico de origen) ── */
interface AggSource {
  fechaTs: number;
  fechaLabel: string;
  medico?: string;
  consultaId?: string;
  esActual: boolean;
  perfil: boolean;
}
interface AggDx extends AggSource { display: string; code?: string; details?: string; veces: number; }
interface AggMed extends AggSource { name: string; item?: FHIRItem; veces: number; }
interface AggOrder extends AggSource { display: string; details?: string; sub: OrderSubType; veces: number; }

/* ── Filas SOAPE para el detalle de consulta ── */
const SOAP_ROWS: Array<{ field: keyof DetalleSoap; letter: string; name: string; cls: 'soapLabelS' | 'soapLabelO' | 'soapLabelA' | 'soapLabelP' | 'soapLabelE' }> = [
  { field: 'subjective', letter: 'S', name: 'Subjetivo', cls: 'soapLabelS' },
  { field: 'objective', letter: 'O', name: 'Objetivo', cls: 'soapLabelO' },
  { field: 'assessment', letter: 'A', name: 'Análisis / Diagnóstico', cls: 'soapLabelA' },
  { field: 'plan', letter: 'P', name: 'Plan', cls: 'soapLabelP' },
  { field: 'evolution', letter: 'E', name: 'Evolución', cls: 'soapLabelE' },
];

/* ── Vital signs extraction from Objective text ── */

interface VitalSign {
  label: string;
  value: string;
  unit: string;
}

function extractVitals(objectiveText: string): VitalSign[] {
  if (!objectiveText) return [];
  const vitals: VitalSign[] = [];
  const patterns: Array<{ label: string; re: RegExp; unit: string }> = [
    { label: 'PA', re: /(?:PA|presi[oó]n\s*arterial|T\.?A\.?)[:\s]*(\d{2,3}\/\d{2,3})/i, unit: 'mmHg' },
    { label: 'FC', re: /(?:FC|frecuencia\s*card[ií]aca|pulso)[:\s]*(\d{2,3})/i, unit: 'lpm' },
    { label: 'FR', re: /(?:FR|frecuencia\s*respiratoria)[:\s]*(\d{1,2})/i, unit: 'rpm' },
    { label: 'Temp', re: /(?:T[eé]mp(?:eratura)?|T°)[:\s]*(\d{2}[.,]\d{1,2})/i, unit: '°C' },
    { label: 'SpO2', re: /(?:SpO2|saturaci[oó]n|sat\.?\s*O2)[:\s]*(\d{2,3})/i, unit: '%' },
    { label: 'Peso', re: /(?:peso)[:\s]*(\d{2,3}(?:[.,]\d{1,2})?)/i, unit: 'kg' },
    { label: 'Talla', re: /(?:talla|estatura)[:\s]*(\d{2,3}(?:[.,]\d{1,2})?)/i, unit: 'cm' },
  ];

  for (const { label, re, unit } of patterns) {
    const match = objectiveText.match(re);
    if (match) vitals.push({ label, value: match[1], unit });
  }
  return vitals;
}

/* ── Date formatters ── */

function tsToDate(ts?: Timestamp): Date | null {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate();
  if (typeof ts === 'object' && '_seconds' in ts) {
    return new Date((ts as unknown as { _seconds: number })._seconds * 1000);
  }
  if (typeof ts === 'object' && 'seconds' in ts) {
    return new Date((ts as unknown as { seconds: number }).seconds * 1000);
  }
  return null;
}

function formatDateShort(ts?: Timestamp): { day: string; month: string } | null {
  const d = tsToDate(ts);
  if (!d || isNaN(d.getTime())) return null;
  return {
    day: String(d.getDate()).padStart(2, '0'),
    month: d.toLocaleDateString('es', { month: 'short' }).replace('.', ''),
  };
}

function formatFullDate(ts?: Timestamp): string {
  const d = tsToDate(ts);
  if (!d || isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es', { day: '2-digit', month: 'long', year: 'numeric' });
}

function formatDuration(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s === 0 ? `${m} min` : `${m}:${String(s).padStart(2, '0')}`;
}

/** Quita marcadores Markdown (**negrita**, #, viñetas) del texto de la IA, que
 *  de otro modo se renderiza pesado / con asteriscos en el detalle. */
function stripMd(s?: string): string {
  return (s ?? '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/^\s*[-*]\s+/gm, '• ')
    .trim();
}
function normName(s?: string): string {
  return (s ?? '').toLowerCase().trim().replace(/\s+/g, ' ');
}

/* ── Consolidación del detalle de una consulta (para la vista inline) ── */

interface DetalleConsolidado {
  soap: DetalleSoap | null;
  fhir: FHIRItem[];
  alerts: string[];
  educacion: string;
}

function consolidarDetalle(d: DetalleConsulta): DetalleConsolidado {
  const notas = d.notas ?? [];
  // SOAP: fusiona TODAS las notas, campo por campo, tomando el texto más completo
  // (evita que falten secciones cuando hay varias notas parciales en la consulta).
  const acc: Record<string, string> = {};
  for (const n of notas) {
    const s = n.soap;
    if (!s) continue;
    for (const k of ['subjective', 'objective', 'assessment', 'plan', 'evolution', 'education'] as const) {
      const v = (s[k] ?? '').trim();
      if (v && v.length > (acc[k] ?? '').length) acc[k] = v;
    }
  }
  const soap: DetalleSoap | null = Object.keys(acc).length ? (acc as DetalleSoap) : null;
  const fhirMap = new Map<string, FHIRItem>();
  for (const n of notas) {
    for (const it of n.fhir ?? []) {
      const key = `${it.type}:${(it.display ?? '').toLowerCase().trim()}`;
      fhirMap.set(key, it);
    }
  }
  const alertSet = new Set<string>();
  for (const n of notas) {
    for (const a of n.alerts ?? []) {
      if (typeof a === 'string' && a.trim()) alertSet.add(a.trim());
    }
  }
  let educacion = '';
  for (let i = notas.length - 1; i >= 0; i--) {
    const e = notas[i].healthEducation;
    const str = Array.isArray(e) ? e.join('\n') : (typeof e === 'string' ? e : '');
    if (str.trim()) { educacion = str; break; }
  }
  return { soap, fhir: Array.from(fhirMap.values()), alerts: Array.from(alertSet), educacion };
}

/* ── Component ── */

type SectionKey =
  | 'resumen' | 'diagnosticos' | 'medicamentos' | 'alergias' | 'vitales'
  | 'ordenes' | 'vacunas' | 'laboratorio' | 'citas' | 'historial';

export function ExpedienteTab({
  pacienteId,
  pacienteUid,
  patientContext,
  pacienteFotoURL,
  fhir,
  currentConsultaId,
}: ExpedienteTabProps) {
  const [active, setActive] = useState<SectionKey>('resumen');

  const [historial, setHistorial] = useState<HistorialItem[]>([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histError, setHistError] = useState<string | null>(null);
  const [openConsultaId, setOpenConsultaId] = useState<string | null>(null);
  const [detalle, setDetalle] = useState<Record<string, DetalleConsulta | null>>({});
  const [detalleLoading, setDetalleLoading] = useState<Record<string, boolean>>({});

  // M12 — datos externos MINSA (vacunas/labs/citas).
  const [vacunas, setVacunas] = useState<ExternalState>({ loading: false, items: [], disponible: false });
  const [labs, setLabs] = useState<ExternalState>({ loading: false, items: [], disponible: false });
  const [citas, setCitas] = useState<ExternalState>({ loading: false, items: [], disponible: false });

  // Histórico de TRIAGE del paciente: interesa saber si en un triage ANTERIOR
  // el paciente fue clasificado en un nivel distinto al de teleconsulta (nivel
  // 1-3 → 911 / presencial). Es una señal clínica para el médico.
  const [triagePrevios, setTriagePrevios] = useState<TriageEventoPrevio[]>([]);
  useEffect(() => {
    const uid = pacienteUid || pacienteId;
    if (!uid) { setTriagePrevios([]); return; }
    let cancelled = false;
    httpsCallable<{ pacienteUid: string; limit: number }, { eventos: TriageEventoPrevio[] }>(
      functions, 'portal_getTriageHistorialPaciente',
    )({ pacienteUid: uid, limit: 20 })
      .then((res) => { if (!cancelled) setTriagePrevios(res.data?.eventos ?? []); })
      .catch(() => { if (!cancelled) setTriagePrevios([]); });
    return () => { cancelled = true; };
  }, [pacienteUid, pacienteId]);

  // Triages previos con derivación DISTINTA a teleconsulta (911 / presencial).
  const triageDerivacionesDistintas = useMemo(
    () => triagePrevios.filter(
      (e) => e.resultado === 'emergencia_911' || e.resultado === 'presencial' ||
        (typeof e.nivel === 'number' && e.nivel <= 3),
    ),
    [triagePrevios],
  );

  useEffect(() => {
    if (!pacienteId) return;
    let cancelled = false;
    const cargar = <T,>(
      cfName: string,
      extract: (data: T) => Record<string, unknown>[],
      setState: (s: ExternalState) => void,
    ) => {
      setState({ loading: true, items: [], disponible: false });
      // El gateway MINSA exige el Bearer token del médico (Firebase idToken) —
      // sin él responde 401 en vacunas/labs/citas.
      (async () => {
        const idToken = await auth.currentUser?.getIdToken().catch(() => undefined);
        if (cancelled) return;
      httpsCallable<{ pacienteId: string; pacienteUid?: string; consultaId?: string; idToken?: string }, T & { noConfigurado?: boolean; error?: string; cedula?: string }>(functions, cfName)({ pacienteId, pacienteUid: pacienteUid ?? undefined, consultaId: currentConsultaId, idToken })
        .then((res) => {
          if (cancelled) return;
          const data = res.data as T & { noConfigurado?: boolean; error?: string; cedula?: string };
          if (data?.noConfigurado) { setState({ loading: false, items: [], disponible: false }); return; }
          setState({
            loading: false,
            items: extract(data),
            disponible: true,
            error: data?.error,
            cedula: data?.cedula,
          });
        })
        .catch((err) => {
          if (!cancelled) {
            setState({
              loading: false, items: [], disponible: false,
              error: err instanceof Error ? err.message : 'No se pudo consultar',
            });
          }
        });
      })();
    };

    cargar<{ vacunas?: Record<string, unknown>[] }>(
      'portal_obtenerVacunasMinsa', (d) => d.vacunas ?? [], setVacunas);
    cargar<{ ordenes?: Record<string, unknown>[] }>(
      'portal_obtenerLabsMinsa', (d) => d.ordenes ?? [], setLabs);
    cargar<{ citas?: Record<string, unknown>[] }>(
      'portal_obtenerCitasSeis', (d) => d.citas ?? [], setCitas);

    return () => { cancelled = true; };
  }, [pacienteId, pacienteUid, currentConsultaId]);

  // Historial de consultas
  useEffect(() => {
    if (!pacienteId) { setHistorial([]); return; }
    let cancelled = false;
    setHistLoading(true);
    setHistError(null);

    const fn = httpsCallable<
      { pacienteId: string; limit: number },
      { consultas: HistorialItem[]; total: number }
    >(functions, 'portal_obtenerHistorialConsultas');

    fn({ pacienteId, limit: 30 })
      .then((res) => {
        if (cancelled) return;
        const items = (res.data.consultas || []).filter(c => c.id !== currentConsultaId);
        setHistorial(items);
      })
      .catch((err) => {
        if (cancelled) return;
        setHistError(err instanceof Error ? err.message : 'No se pudo cargar el historial.');
      })
      .finally(() => { if (!cancelled) setHistLoading(false); });

    return () => { cancelled = true; };
  }, [pacienteId, currentConsultaId]);

  // Carga perezosa del detalle de una consulta
  const fetchDetalle = useCallback(async (id: string) => {
    if (detalle[id] !== undefined || detalleLoading[id]) return;
    setDetalleLoading(prev => ({ ...prev, [id]: true }));
    try {
      const fn = httpsCallable<{ consultaId: string }, DetalleConsulta>(
        functions, 'portal_obtenerDetalleConsulta',
      );
      const res = await fn({ consultaId: id });
      setDetalle(prev => ({ ...prev, [id]: res.data }));
    } catch {
      setDetalle(prev => ({ ...prev, [id]: null }));
    } finally {
      setDetalleLoading(prev => ({ ...prev, [id]: false }));
    }
  }, [detalle, detalleLoading]);

  // Pre-cargar el detalle de las consultas recientes (hasta 12) para poder agregar
  // diagnósticos/medicamentos/órdenes con su fecha + médico de origen.
  useEffect(() => {
    historial.slice(0, 12).forEach((h) => fetchDetalle(h.id));
  }, [historial, fetchDetalle]);

  const openHistorial = useCallback((id: string) => {
    setOpenConsultaId(id);
    fetchDetalle(id);
  }, [fetchDetalle]);

  // Categorise FHIR items from current consultation
  const { diagnoses, medications, orders } = useMemo(() => {
    const dx: FHIRItem[] = [];
    const meds: FHIRItem[] = [];
    const ords: FHIRItem[] = [];
    for (const item of fhir) {
      if (['allergy', 'familyHistory', 'personalHistory'].includes(item.type)) continue;
      const cat = categorise(item);
      if (cat === 'diagnosis') dx.push(item);
      else if (cat === 'medication') meds.push(item);
      else if (cat === 'order') ords.push(item);
    }
    return { diagnoses: dx, medications: meds, orders: ords };
  }, [fhir]);

  const vitals = useMemo(() => {
    const allObjectiveText = fhir
      .filter(f => f.type === 'condition')
      .map(f => f.details || '')
      .join(' ');
    return extractVitals(allObjectiveText);
  }, [fhir]);

  const allConditions = useMemo(() => {
    const fromProfile = patientContext.conditions || [];
    const fromFhir = diagnoses.map(d => d.display);
    return Array.from(new Set([...fromProfile, ...fromFhir]));
  }, [patientContext.conditions, diagnoses]);

  const allMedications = useMemo(() => {
    const fromProfile = patientContext.medications || [];
    const seen = new Set<string>();
    const result: Array<{ name: string; fhirItem?: FHIRItem; fromProfile: boolean }> = [];
    for (const med of medications) {
      const key = med.display.toLowerCase();
      if (!seen.has(key)) { seen.add(key); result.push({ name: med.display, fhirItem: med, fromProfile: false }); }
    }
    for (const name of fromProfile) {
      const key = name.toLowerCase();
      if (!seen.has(key)) { seen.add(key); result.push({ name, fromProfile: true }); }
    }
    return result;
  }, [patientContext.medications, medications]);

  // ── Agregación a través del historial: cada diagnóstico/medicamento/orden con
  //    su FECHA + MÉDICO de origen, deduplicado por nombre y ordenado por reciente. ──
  const diagnosesAgg = useMemo(() => {
    const map = new Map<string, AggDx>();
    const add = (display: string, code: string | undefined, details: string | undefined, m: AggSource) => {
      const key = normName(display);
      if (!key) return;
      const prev = map.get(key);
      if (!prev) { map.set(key, { display, code, details, veces: 1, ...m }); return; }
      prev.veces += 1;
      if (m.fechaTs > prev.fechaTs) Object.assign(prev, { code: code ?? prev.code, details: details ?? prev.details, ...m });
    };
    for (const dx of diagnoses) add(dx.display, dx.code, dx.details, { fechaTs: Infinity, fechaLabel: 'Consulta actual', consultaId: currentConsultaId, esActual: true, perfil: false });
    for (const h of historial) {
      const d = detalle[h.id]; if (!d) continue;
      const ts = tsToDate(h.finalizadaEn ?? h.creadoEn)?.getTime() ?? 0;
      const label = formatFullDate(h.finalizadaEn ?? h.creadoEn);
      for (const it of consolidarDetalle(d).fhir) {
        if (['allergy', 'familyHistory', 'personalHistory', 'medication'].includes(it.type)) continue;
        if (categorise(it) !== 'diagnosis') continue;
        add(it.display, it.code, it.details, { fechaTs: ts, fechaLabel: label, medico: h.medicoNombre, consultaId: h.id, esActual: false, perfil: false });
      }
    }
    for (const c of patientContext.conditions || []) {
      const key = normName(c);
      if (key && !map.has(key)) map.set(key, { display: c, veces: 1, fechaTs: -1, fechaLabel: '', esActual: false, perfil: true });
    }
    return Array.from(map.values()).sort((a, b) => b.fechaTs - a.fechaTs);
  }, [diagnoses, historial, detalle, patientContext.conditions, currentConsultaId]);

  const medsAgg = useMemo(() => {
    const map = new Map<string, AggMed>();
    const add = (name: string, item: FHIRItem | undefined, m: AggSource) => {
      const key = normName(name);
      if (!key) return;
      const prev = map.get(key);
      if (!prev) { map.set(key, { name, item, veces: 1, ...m }); return; }
      prev.veces += 1;
      if (m.fechaTs > prev.fechaTs) Object.assign(prev, { item: item ?? prev.item, ...m });
    };
    for (const md of medications) add(md.display, md, { fechaTs: Infinity, fechaLabel: 'Consulta actual', consultaId: currentConsultaId, esActual: true, perfil: false });
    for (const h of historial) {
      const d = detalle[h.id]; if (!d) continue;
      const ts = tsToDate(h.finalizadaEn ?? h.creadoEn)?.getTime() ?? 0;
      const label = formatFullDate(h.finalizadaEn ?? h.creadoEn);
      for (const it of consolidarDetalle(d).fhir) {
        if (categorise(it) !== 'medication') continue;
        add(it.display, it, { fechaTs: ts, fechaLabel: label, medico: h.medicoNombre, consultaId: h.id, esActual: false, perfil: false });
      }
    }
    for (const name of patientContext.medications || []) {
      const key = normName(name);
      if (key && !map.has(key)) map.set(key, { name, veces: 1, fechaTs: -1, fechaLabel: '', esActual: false, perfil: true });
    }
    return Array.from(map.values()).sort((a, b) => b.fechaTs - a.fechaTs);
  }, [medications, historial, detalle, patientContext.medications, currentConsultaId]);

  const ordersAgg = useMemo(() => {
    const map = new Map<string, AggOrder>();
    const add = (item: FHIRItem, m: AggSource) => {
      const nm = normName(item.display);
      if (!nm) return;
      const key = `${orderSubType(item)}:${nm}`;
      const prev = map.get(key);
      if (!prev) { map.set(key, { display: item.display, details: item.details, sub: orderSubType(item), veces: 1, ...m }); return; }
      prev.veces += 1;
      if (m.fechaTs > prev.fechaTs) Object.assign(prev, { details: item.details ?? prev.details, ...m });
    };
    for (const o of orders) add(o, { fechaTs: Infinity, fechaLabel: 'Consulta actual', consultaId: currentConsultaId, esActual: true, perfil: false });
    for (const h of historial) {
      const d = detalle[h.id]; if (!d) continue;
      const ts = tsToDate(h.finalizadaEn ?? h.creadoEn)?.getTime() ?? 0;
      const label = formatFullDate(h.finalizadaEn ?? h.creadoEn);
      for (const it of consolidarDetalle(d).fhir) {
        if (categorise(it) !== 'order') continue;
        add(it, { fechaTs: ts, fechaLabel: label, medico: h.medicoNombre, consultaId: h.id, esActual: false, perfil: false });
      }
    }
    return Array.from(map.values()).sort((a, b) => b.fechaTs - a.fechaTs);
  }, [orders, historial, detalle, currentConsultaId]);

  // ── Resumen clínico EN PROSA ──
  const resumen = useMemo<string[]>(() => {
    const p = patientContext;
    const paras: string[] = [];
    const nombre = p.name || 'El paciente';

    // 1. Identidad
    const ident: string[] = [];
    if (p.age > 0) ident.push(`${p.age} años`);
    if (p.gender) ident.push(`sexo ${p.gender.toLowerCase()}`);
    paras.push(
      ident.length > 0
        ? `${nombre}, ${ident.join(', ')}.`
        : `${nombre}.`,
    );

    // 2. Alergias (seguridad primero)
    paras.push(
      p.allergies.length > 0
        ? `Presenta alergia${p.allergies.length > 1 ? 's' : ''} a ${p.allergies.join(', ')}.`
        : 'No registra alergias conocidas.',
    );

    // 3. Diagnósticos / condiciones
    paras.push(
      allConditions.length > 0
        ? `Antecedentes y diagnósticos relevantes: ${allConditions.join(', ')}.`
        : 'No registra diagnósticos crónicos en el sistema.',
    );

    // 4. Medicamentos
    const medNames = allMedications.map(m => m.name);
    paras.push(
      medNames.length > 0
        ? `Tratamiento farmacológico actual: ${medNames.join(', ')}.`
        : 'No registra medicación habitual.',
    );

    return paras;
  }, [patientContext, allConditions, allMedications]);

  // Última consulta (para la tarjeta del resumen) + su impresión diagnóstica.
  const ultimaConsulta = historial[0];
  const ultimaSoap = useMemo(() => {
    const u = historial[0];
    const d = u ? detalle[u.id] : undefined;
    return d ? consolidarDetalle(d).soap : null;
  }, [historial, detalle]);

  // ── Definición de secciones de navegación ──
  const sections = useMemo(() => {
    const list: Array<{ key: SectionKey; label: string; icon: ReactNode; count?: number }> = [
      { key: 'resumen', label: 'Resumen', icon: <Sparkles size={15} /> },
      { key: 'diagnosticos', label: 'Diagnósticos', icon: <Stethoscope size={15} />, count: diagnosesAgg.length },
      { key: 'medicamentos', label: 'Medicamentos', icon: <Pill size={15} />, count: medsAgg.length },
      { key: 'alergias', label: 'Alergias', icon: <ShieldAlert size={15} />, count: patientContext.allergies.length },
    ];
    if (vitals.length > 0) list.push({ key: 'vitales', label: 'Signos vitales', icon: <Activity size={15} />, count: vitals.length });
    if (ordersAgg.length > 0) list.push({ key: 'ordenes', label: 'Órdenes', icon: <Microscope size={15} />, count: ordersAgg.length });
    list.push({ key: 'vacunas', label: 'Vacunas', icon: <Syringe size={15} />, count: vacunas.disponible ? vacunas.items.length : undefined });
    list.push({ key: 'laboratorio', label: 'Laboratorio', icon: <FlaskConical size={15} />, count: labs.disponible ? labs.items.length : undefined });
    list.push({ key: 'citas', label: 'Próximas citas', icon: <Calendar size={15} />, count: citas.disponible ? citas.items.length : undefined });
    list.push({ key: 'historial', label: 'Historial', icon: <FileText size={15} />, count: historial.length });
    return list;
  }, [diagnosesAgg.length, medsAgg.length, patientContext.allergies.length, vitals.length, ordersAgg.length, vacunas, labs, citas, historial.length]);

  const activeLabel = sections.find(s => s.key === active)?.label ?? '';

  return (
    <div className={styles.root}>
      {/* ── Navegación lateral ── */}
      <nav className={styles.nav} aria-label="Secciones del expediente">
        {sections.map(s => (
          <button
            key={s.key}
            type="button"
            className={`${styles.navItem} ${active === s.key ? styles.navItemActive : ''}`}
            onClick={() => { setActive(s.key); if (s.key !== 'historial') setOpenConsultaId(null); }}
          >
            <span className={styles.navIcon}>{s.icon}</span>
            <span className={styles.navLabel}>{s.label}</span>
            {typeof s.count === 'number' && s.count > 0 && (
              <span className={styles.navCount}>{s.count}</span>
            )}
          </button>
        ))}
      </nav>

      {/* ── Contenido (scroll propio) ── */}
      <div className={styles.content}>
        <h3 className={styles.contentTitle}>{activeLabel}</h3>

        {/* Aviso: el paciente marcó antes un nivel de triage DISTINTO al de
            teleconsulta (911 / presencial). Señal clínica para el médico. */}
        {triageDerivacionesDistintas.length > 0 && (
          <div
            role="alert"
            style={{
              display: 'flex', gap: 10, alignItems: 'flex-start',
              margin: '0 0 14px', padding: '10px 14px', borderRadius: 10,
              background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.35)',
              color: 'var(--tc-warn, #8a6d1f)', fontSize: 13,
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1.2 }}>⚠️</span>
            <div>
              <strong>Triage previo con derivación distinta a teleconsulta.</strong>
              <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {triageDerivacionesDistintas.slice(0, 3).map((e) => {
                  const etiqueta = e.resultado === 'emergencia_911' ? 'Emergencia (911)'
                    : e.resultado === 'presencial' ? 'Atención presencial'
                    : `Nivel ${e.nivel}${e.nivelNombre ? ` · ${e.nivelNombre}` : ''}`;
                  return (
                    <span key={e.id}>
                      {e.fecha ? `${e.fecha}: ` : ''}{etiqueta}
                      {e.motivoConsulta ? ` — “${e.motivoConsulta.slice(0, 80)}”` : ''}
                    </span>
                  );
                })}
                {triageDerivacionesDistintas.length > 3 && (
                  <span>y {triageDerivacionesDistintas.length - 3} más…</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Resumen ── */}
        {active === 'resumen' && (
          <div className={styles.resumen}>
            {/* Cabecera del paciente */}
            <div className={styles.hero}>
              <Avatar className={styles.heroAvatar} nombre={patientContext.name} fotoURL={pacienteFotoURL} zoomable />
              <div className={styles.heroInfo}>
                <h4 className={styles.heroName}>{patientContext.name || 'Paciente'}</h4>
                <div className={styles.heroChips}>
                  {patientContext.age > 0 && (
                    <span className={styles.heroChip}><Calendar size={11} aria-hidden="true" /> {patientContext.age} años</span>
                  )}
                  {patientContext.gender && (
                    <span className={styles.heroChip}><User size={11} aria-hidden="true" /> {patientContext.gender}</span>
                  )}
                  {pacienteId && (
                    <span className={styles.heroChip}><FileText size={11} aria-hidden="true" /> {pacienteId}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Indicadores rápidos */}
            <div className={styles.statGrid}>
              <div className={`${styles.statCard} ${styles.statDx}`}>
                <span className={styles.statIcon}><Stethoscope size={16} /></span>
                <span className={styles.statValue}>{allConditions.length}</span>
                <span className={styles.statLabel}>Diagnósticos</span>
              </div>
              <div className={`${styles.statCard} ${styles.statMed}`}>
                <span className={styles.statIcon}><Pill size={16} /></span>
                <span className={styles.statValue}>{allMedications.length}</span>
                <span className={styles.statLabel}>Medicamentos</span>
              </div>
              <div className={`${styles.statCard} ${styles.statAllergy}`}>
                <span className={styles.statIcon}><ShieldAlert size={16} /></span>
                <span className={styles.statValue}>{patientContext.allergies.length}</span>
                <span className={styles.statLabel}>Alergias</span>
              </div>
              <div className={`${styles.statCard} ${styles.statHist}`}>
                <span className={styles.statIcon}><FileText size={16} /></span>
                <span className={styles.statValue}>{historial.length}</span>
                <span className={styles.statLabel}>Consultas</span>
              </div>
            </div>

            {/* Alergias (seguridad) */}
            {patientContext.allergies.length > 0 ? (
              <div className={styles.alertCard}>
                <ShieldAlert size={16} className={styles.alertCardIcon} aria-hidden="true" />
                <div className={styles.alertCardBody}>
                  <span className={styles.alertCardTitle}>Alergias</span>
                  <div className={styles.alertCardChips}>
                    {patientContext.allergies.map((a, i) => (
                      <span key={i} className={styles.alertChip}>{a}</span>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className={styles.okCard}><Heart size={14} aria-hidden="true" /> Sin alergias conocidas</div>
            )}

            {/* Resumen clínico (prosa) */}
            <div className={styles.card}>
              <div className={styles.cardHead}><Sparkles size={13} aria-hidden="true" /> Resumen clínico</div>
              <div className={styles.prose}>
                {resumen.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>

            {/* Última consulta */}
            <div className={styles.card}>
              <div className={styles.cardHead}><FileText size={13} aria-hidden="true" /> Última consulta</div>
              {ultimaConsulta ? (
                <div className={styles.lastVisit}>
                  <div className={styles.lastVisitTop}>
                    <span className={styles.lastVisitMotivo}>{ultimaConsulta.motivoConsulta || 'Consulta'}</span>
                    <button
                      type="button"
                      className={styles.lastVisitBtn}
                      onClick={() => { setActive('historial'); openHistorial(ultimaConsulta.id); }}
                    >
                      Ver consulta <ChevronRight size={12} aria-hidden="true" />
                    </button>
                  </div>
                  <div className={styles.lastVisitMeta}>
                    {(ultimaConsulta.finalizadaEn || ultimaConsulta.creadoEn) && (
                      <span><Calendar size={11} aria-hidden="true" /> {formatFullDate(ultimaConsulta.finalizadaEn ?? ultimaConsulta.creadoEn)}</span>
                    )}
                    {ultimaConsulta.medicoNombre && (
                      <span><Stethoscope size={11} aria-hidden="true" /> {ultimaConsulta.medicoNombre}</span>
                    )}
                    {ultimaConsulta.duracionSegundos != null && (
                      <span><Clock size={11} aria-hidden="true" /> {formatDuration(ultimaConsulta.duracionSegundos)}</span>
                    )}
                  </div>
                  {ultimaSoap?.assessment && (
                    <p className={styles.lastVisitDx}><strong>Impresión diagnóstica:</strong> {ultimaSoap.assessment}</p>
                  )}
                </div>
              ) : (
                <p className={styles.emptyItems} style={{ padding: '14px' }}>Sin consultas previas registradas.</p>
              )}
            </div>
          </div>
        )}

        {/* ── Diagnósticos ── */}
        {active === 'diagnosticos' && (
          diagnosesAgg.length > 0 ? (
            <div className={styles.cardList}>
              {diagnosesAgg.map((dx, i) => (
                <article key={i} className={`${styles.recordCard} ${styles.recordDx}`}>
                  <div className={styles.recordTop}>
                    <h4 className={styles.recordName}>{dx.display}</h4>
                    {dx.code && ICD10_RE.test(dx.code) && <span className={styles.recordCode}>CIE-10 {dx.code}</span>}
                  </div>
                  {dx.details && normName(dx.details) !== normName(dx.display) && (
                    <p className={styles.recordDetail}>{stripMd(dx.details)}</p>
                  )}
                  <div className={styles.recordMeta}>
                    {dx.esActual ? (
                      <span className={styles.recordTagActual}><Sparkles size={11} /> Consulta actual</span>
                    ) : dx.perfil ? (
                      <span className={styles.recordTagPerfil}><User size={11} /> Antecedente del paciente</span>
                    ) : (
                      <>
                        {dx.fechaLabel && <span><Calendar size={11} /> {dx.fechaLabel}</span>}
                        {dx.medico && <span><Stethoscope size={11} /> {dx.medico}</span>}
                      </>
                    )}
                    {dx.veces > 1 && <span className={styles.recordVeces}>{dx.veces} consultas</span>}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <p className={styles.emptyItems}>Sin diagnósticos registrados</p>
          )
        )}

        {/* ── Medicamentos ── */}
        {active === 'medicamentos' && (
          medsAgg.length > 0 ? (
            <div className={styles.cardList}>
              {medsAgg.map((med, i) => {
                const it = med.item;
                const tipo = it?.prescriptionType;
                return (
                  <article key={i} className={`${styles.recordCard} ${styles.recordMed}`}>
                    <div className={styles.recordTop}>
                      <h4 className={styles.recordName}>{med.name}</h4>
                      {tipo === 'recomendacion' && <span className={`${styles.rxTag} ${styles.rxRec}`}>Recomendación</span>}
                      {tipo === 'reportado_paciente' && <span className={`${styles.rxTag} ${styles.rxRep}`}>Refiere tomar</span>}
                    </div>
                    {it && (it.dose || it.presentation || it.frequency || it.duration || it.route) && (
                      <div className={styles.rxGrid}>
                        {it.dose && <span className={styles.rxField}><span className={styles.rxFieldLabel}>Dosis</span>{it.dose}</span>}
                        {it.presentation && <span className={styles.rxField}><span className={styles.rxFieldLabel}>Presentación</span>{it.presentation}</span>}
                        {it.frequency && <span className={styles.rxField}><span className={styles.rxFieldLabel}>Frecuencia</span>{it.frequency}</span>}
                        {it.duration && <span className={styles.rxField}><span className={styles.rxFieldLabel}>Duración</span>{it.duration}</span>}
                        {it.route && <span className={styles.rxField}><span className={styles.rxFieldLabel}>Vía</span>{it.route}</span>}
                      </div>
                    )}
                    {it?.details && normName(it.details) !== normName(med.name) && (
                      <p className={styles.recordDetail}>{stripMd(it.details)}</p>
                    )}
                    <div className={styles.recordMeta}>
                      {med.esActual ? (
                        <span className={styles.recordTagActual}><Sparkles size={11} /> Consulta actual</span>
                      ) : med.perfil ? (
                        <span className={styles.recordTagPerfil}><User size={11} /> Medicación habitual</span>
                      ) : (
                        <>
                          {med.fechaLabel && <span><Calendar size={11} /> {med.fechaLabel}</span>}
                          {med.medico && <span><Stethoscope size={11} /> {med.medico}</span>}
                        </>
                      )}
                      {med.veces > 1 && <span className={styles.recordVeces}>{med.veces} consultas</span>}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <p className={styles.emptyItems}>Sin medicamentos registrados</p>
          )
        )}

        {/* ── Alergias ── */}
        {active === 'alergias' && (
          patientContext.allergies.length > 0 ? (
            <div className={styles.cardList}>
              {patientContext.allergies.map((a, i) => (
                <article key={i} className={`${styles.recordCard} ${styles.recordAllergy}`}>
                  <span className={styles.allergyIcon}><ShieldAlert size={17} /></span>
                  <div className={styles.allergyBody}>
                    <h4 className={styles.recordName}>{a}</h4>
                    <span className={styles.allergyHint}>Verificar antes de prescribir o aplicar tratamientos.</span>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className={styles.okCardBig}>
              <Heart size={18} aria-hidden="true" />
              <div>
                <strong>Sin alergias conocidas</strong>
                <span>No hay alergias registradas para este paciente.</span>
              </div>
            </div>
          )
        )}

        {/* ── Signos vitales ── */}
        {active === 'vitales' && (
          vitals.length > 0 ? (
            <div className={styles.vitalsGrid}>
              {vitals.map(v => (
                <div key={v.label} className={styles.vitalCard}>
                  <span className={styles.vitalLabel}>{v.label}</span>
                  <span className={styles.vitalValue}>{v.value}</span>
                  <span className={styles.vitalUnit}>{v.unit}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className={styles.emptyItems}>Sin signos vitales registrados</p>
          )
        )}

        {/* ── Órdenes ── */}
        {active === 'ordenes' && (
          ordersAgg.length > 0 ? (
            <div className={styles.cardList}>
              {ordersAgg.map((ord, i) => (
                <article key={i} className={`${styles.recordCard} ${styles.recordOrd}`}>
                  <div className={styles.recordTop}>
                    <span className={`${styles.ordTypeTag} ${styles[`ord_${ord.sub}`]}`}>{ORDER_LABELS[ord.sub]}</span>
                    <h4 className={styles.recordName}>{ord.display}</h4>
                  </div>
                  {ord.details && normName(ord.details) !== normName(ord.display) && (
                    <p className={styles.recordDetail}>{stripMd(ord.details)}</p>
                  )}
                  <div className={styles.recordMeta}>
                    {ord.esActual ? (
                      <span className={styles.recordTagActual}><Sparkles size={11} /> Consulta actual</span>
                    ) : (
                      <>
                        {ord.fechaLabel && <span><Calendar size={11} /> {ord.fechaLabel}</span>}
                        {ord.medico && <span><Stethoscope size={11} /> {ord.medico}</span>}
                      </>
                    )}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <p className={styles.emptyItems}>Sin órdenes registradas</p>
          )
        )}

        {/* ── Vacunas / Laboratorio / Citas (MINSA) ── */}
        {active === 'vacunas' && <VacunasBody state={vacunas} paciente={{ edad: patientContext.age, sexo: patientContext.gender }} />}
        {active === 'laboratorio' && <LabsBody state={labs} paciente={{ edad: patientContext.age, sexo: patientContext.gender }} consultaId={currentConsultaId} />}
        {active === 'citas' && <CitasBody state={citas} />}

        {/* ── Historial de consultas ── */}
        {active === 'historial' && (
          openConsultaId ? (
            <ConsultaDetalleInline
              consultaId={openConsultaId}
              item={historial.find(h => h.id === openConsultaId)}
              detail={detalle[openConsultaId]}
              loading={!!detalleLoading[openConsultaId]}
              onBack={() => setOpenConsultaId(null)}
            />
          ) : (
            <>
              {histLoading && (
                <div className={styles.loadingState}>
                  <Loader2 size={16} className={styles.spin} /> <span>Cargando historial...</span>
                </div>
              )}
              {histError && !histLoading && (
                <div className={styles.errorState}><AlertCircle size={14} /> <span>{histError}</span></div>
              )}
              {!histLoading && !histError && historial.length === 0 && (
                <p className={styles.emptyItems}>Sin consultas previas registradas</p>
              )}
              {!histLoading && !histError && historial.length > 0 && (
                <div className={styles.historyList}>
                  {historial.map(c => {
                    const dateParts = formatDateShort(c.finalizadaEn ?? c.creadoEn);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        className={styles.historyCard}
                        onClick={() => openHistorial(c.id)}
                      >
                        <div className={styles.historyDateBadge}>
                          <span className={styles.historyDay}>{dateParts?.day ?? '—'}</span>
                          {dateParts && <span className={styles.historyMonth}>{dateParts.month}</span>}
                        </div>
                        <div className={styles.historyInfo}>
                          <p className={styles.historyMotivo}>{c.motivoConsulta || 'Consulta sin motivo'}</p>
                          <p className={styles.historyMeta}>
                            {c.medicoNombre && <span><Stethoscope size={10} /> {c.medicoNombre}</span>}
                            {c.duracionSegundos != null && <span><Clock size={10} /> {formatDuration(c.duracionSegundos)}</span>}
                            {c.salaNombre && <span>{c.salaNombre}</span>}
                          </p>
                        </div>
                        <ChevronRight size={16} className={styles.historyChevron} />
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )
        )}
      </div>
    </div>
  );
}

/* ── Detalle de consulta inline (todas las secciones, como en /historial) ── */

function ConsultaDetalleInline({
  consultaId, item, detail, loading, onBack,
}: {
  consultaId: string;
  item?: HistorialItem;
  detail: DetalleConsulta | null | undefined;
  loading: boolean;
  onBack: () => void;
}) {
  const c = detail?.consulta;
  const con = detail ? consolidarDetalle(detail) : null;
  const soap = con?.soap;
  const hasSoap = !!(soap && (soap.subjective || soap.objective || soap.assessment || soap.plan || soap.evolution));
  const motivo = c?.motivoConsulta || item?.motivoConsulta;
  const medico = c?.medicoNombre || item?.medicoNombre;
  const fecha = formatFullDate(c?.finalizadaEn ?? c?.iniciadaEn ?? item?.finalizadaEn ?? item?.creadoEn);
  const dur = c?.duracionSegundos ?? item?.duracionSegundos;

  return (
    <div className={styles.detalleWrap}>
      <div className={styles.detalleTopBar}>
        <button type="button" className={styles.backBtn} onClick={onBack}>
          <ArrowLeft size={14} /> Volver al historial
        </button>
        <a
          href={`/historial/${consultaId}`}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.verConsultaBtn}
        >
          <ExternalLink size={13} /> Ver completa
        </a>
      </div>

      {loading ? (
        <div className={styles.loadingState}>
          <Loader2 size={16} className={styles.spin} /> <span>Cargando detalle...</span>
        </div>
      ) : detail === null ? (
        <div className={styles.errorState}>
          <AlertCircle size={14} /> <span>No se pudo cargar el detalle de esta consulta.</span>
        </div>
      ) : (
        <>
          {/* Cabecera */}
          <div className={styles.detalleHead}>
            <p className={styles.detalleMotivo}>{motivo || 'Consulta'}</p>
            <p className={styles.detalleMeta}>
              {fecha && <span><Calendar size={11} /> {fecha}</span>}
              {medico && <span><Stethoscope size={11} /> {medico}</span>}
              {dur != null && <span><Clock size={11} /> {formatDuration(dur)}</span>}
            </p>
          </div>

          {/* Nota clínica SOAPE */}
          {hasSoap ? (
            <section className={styles.detalleSection}>
              <h4 className={styles.detalleSectionTitle}><Brain size={13} /> Nota clínica (SOAPE)</h4>
              <div className={styles.soapSummary}>
                {SOAP_ROWS.map((r) => {
                  const text = soap?.[r.field];
                  if (!text || !text.trim()) return null;
                  return (
                    <div key={r.field} className={styles.soapRow}>
                      <span className={`${styles.soapLabel} ${styles[r.cls]}`}>{r.letter}</span>
                      <div className={styles.soapBody}>
                        <span className={styles.soapName}>{r.name}</span>
                        <p className={styles.soapValue}>{stripMd(text)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ) : item?.notas ? (
            <section className={styles.detalleSection}>
              <h4 className={styles.detalleSectionTitle}><FileText size={13} /> Notas</h4>
              <p className={styles.soapValue}>{item.notas}</p>
            </section>
          ) : null}

          {/* Hallazgos clínicos FHIR */}
          {con && con.fhir.length > 0 && (
            <section className={styles.detalleSection}>
              <h4 className={styles.detalleSectionTitle}><Activity size={13} /> Hallazgos clínicos ({con.fhir.length})</h4>
              <ul className={styles.fhirList}>
                {con.fhir.map((it, i) => (
                  <li key={i} className={styles.fhirItem}>
                    <span className={styles.fhirType}>{(it.type || '').toUpperCase()}</span>
                    <span className={styles.fhirDisplay}>{it.display}</span>
                    {it.details && it.details !== it.display && <span className={styles.fhirDetails}>{it.details}</span>}
                    {it.code && <code className={styles.fhirCode}>{it.code}</code>}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Alertas */}
          {con && con.alerts.length > 0 && (
            <section className={styles.detalleSection}>
              <h4 className={styles.detalleSectionTitle}><AlertTriangle size={13} /> Alertas clínicas ({con.alerts.length})</h4>
              <ul className={styles.alertsList}>
                {con.alerts.map((a, i) => (
                  <li key={i} className={styles.alertItem}><AlertTriangle size={12} /> {a}</li>
                ))}
              </ul>
            </section>
          )}

          {/* Educación al paciente */}
          {con && con.educacion && (
            <section className={styles.detalleSection}>
              <h4 className={styles.detalleSectionTitle}><BookOpen size={13} /> Educación al paciente</h4>
              <p className={styles.educationText}>{con.educacion}</p>
            </section>
          )}

          {/* Resumen de la conversación */}
          {detail?.transcripcion && detail.transcripcion.segmentos > 0 && (
            <section className={styles.detalleSection}>
              <h4 className={styles.detalleSectionTitle}>
                <MessageSquareText size={13} /> Conversación ({detail.transcripcion.segmentos} fragmentos)
              </h4>
              <div className={styles.txSummary}>
                {detail.transcripcion.porHablante.map((h, i) => (
                  <div key={i} className={styles.txSummaryRow}>
                    <span className={styles.txSummarySpeaker}>{h.hablante}</span>
                    <p className={styles.txSummaryExtract}>{h.extracto}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Vacío */}
          {!hasSoap && (!con || (con.fhir.length === 0 && con.alerts.length === 0 && !con.educacion)) && !item?.notas && (
            <p className={styles.emptyItems}>No hay información clínica adicional registrada para esta consulta.</p>
          )}
        </>
      )}
    </div>
  );
}
